import { useCallback, useMemo } from "react";
import { Alert, Button, Group, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconRefresh } from "@tabler/icons-react";
import { PortalAccessGrant } from "../../../services/admin/portalAccess.service";
import { useRestorePortalAccessGrant } from "../../../hooks/admin/usePortalAccess";
import {
  classifyPortalGrantError,
  EXISTING_GRANT_ERROR_FALLBACKS,
} from "../../../utils/portalAccessErrors";

interface RevokedGrantCardProps {
  grant: PortalAccessGrant;
  onRestored: () => void;
}

function formatRevokedDate(value: string | null): string | null {
  if (!value) return null;
  return new Date(value).toLocaleDateString();
}

export function RevokedGrantCard({ grant, onRestored }: RevokedGrantCardProps) {
  const restoreGrant = useRestorePortalAccessGrant();

  const displayName = useMemo(
    () =>
      [grant.firstName, grant.lastName].filter(Boolean).join(" ").trim() ||
      grant.portalIdentity.email,
    [grant.firstName, grant.lastName, grant.portalIdentity.email]
  );

  const revokedDateLabel = useMemo(
    () => formatRevokedDate(grant.revokedAt),
    [grant.revokedAt]
  );

  const handleRestore = useCallback(() => {
    restoreGrant.mutate(grant.id, {
      onSuccess: () => {
        notifications.show({ color: "green", message: "Access restored" });
        onRestored();
      },
      onError: (error) => {
        notifications.show({
          color: "red",
          message: classifyPortalGrantError(
            error,
            EXISTING_GRANT_ERROR_FALLBACKS
          ),
        });
      },
    });
  }, [grant.id, restoreGrant, onRestored]);

  return (
    <Alert
      color="yellow"
      variant="light"
      title="Previously removed contact"
      role="status"
    >
      <Group justify="space-between" align="center" wrap="nowrap" gap="md">
        <div>
          <Text size="sm" fw={500}>
            {grant.portalIdentity.email}
          </Text>
          <Text size="xs" c="dimmed">
            {displayName}
            {revokedDateLabel ? ` · Access revoked ${revokedDateLabel}` : ""}
          </Text>
        </div>
        <Button
          size="xs"
          variant="light"
          leftSection={<IconRefresh size={14} />}
          loading={restoreGrant.isPending}
          onClick={handleRestore}
        >
          Restore
        </Button>
      </Group>
    </Alert>
  );
}
